import { useMemo } from "react";
import { BackupBar } from "@/components/BackupBar";
import { ToolContainer } from "@/components/ToolLayout";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ToolInfoSection } from "@/components/ToolInfoSection";
import { addDays, dayDiff, parseLocalDate, useStored, useTrip } from "./tripStore";

const DEFAULT_TASKS: { task: string; daysBefore: number }[] = [
  { task: "Book flights", daysBefore: 60 },
  { task: "Book accommodation", daysBefore: 45 },
  { task: "Check passport validity", daysBefore: 30 },
  { task: "Arrange travel insurance", daysBefore: 21 },
  { task: "Notify bank of travel", daysBefore: 14 },
  { task: "Arrange pet/house sitting", daysBefore: 14 },
  { task: "Check-in for flights", daysBefore: 1 },
  { task: "Pack bags", daysBefore: 1 },
  { task: "Charge devices, print documents", daysBefore: 1 },
];

const TOOL_INFO_CONTENT = {
  what: `Shows the number of days until your departure and schedules common pre-trip tasks — booking flights, arranging insurance, notifying your bank — based on how far in advance each one typically needs to happen. Tick tasks off as you finish them; overdue tasks stay highlighted until you do.`,
  how: `Enter your departure date (it's shared with the other travel tools) and a schedule of tasks sorted by due date appears. Tick each task when it's done. Your date and ticks are kept for this visit only.`,
  tip: `The tasks closest to departure (packing, checking in for flights) tend to get remembered on their own — it's the tasks 4-8 weeks out (insurance, bank notifications) that are easiest to forget entirely without a reminder like this.`,
};

export default function TravelCountdownPlanner() {
  const [trip, setTrip] = useTrip();
  const [done, setDone, resetDone] = useStored<string[]>("fdp-countdown-done-v1", []);
  const today = new Date();
  const dep = parseLocalDate(trip.start);
  const daysUntil = dep ? dayDiff(today, dep) : null;

  const schedule = useMemo(() => {
    if (!dep) return [];
    return DEFAULT_TASKS.map((t) => {
      const due = addDays(dep, -t.daysBefore);
      return { ...t, due, offset: dayDiff(new Date(), due) };
    }).sort((a, b) => a.due.getTime() - b.due.getTime());
  }, [trip.start]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = (t: string) => setDone((d) => (d.includes(t) ? d.filter((x) => x !== t) : [...d, t]));
  const label = (n: number | null) => n === null ? "" : n > 1 ? "days until departure" : n === 1 ? "day until departure" : n === 0 ? "departure is today" : "trip has started";

  return (
    <ToolContainer>
      <Label htmlFor="dep" className="mb-2 block">Departure Date</Label>
      <Input id="dep" type="date" value={trip.start} onChange={(e) => setTrip({ ...trip, start: e.target.value })} className="max-w-xs mb-4" />

      {dep ? (
        <Card className="p-6 text-center mb-6">
          <p className="text-4xl font-bold text-primary" data-testid="tc-days">{daysUntil !== null && daysUntil > 0 ? daysUntil : daysUntil === 0 ? "Today" : 0}</p>
          <p className="text-sm text-muted-foreground mt-1">{label(daysUntil)}</p>
        </Card>
      ) : <p className="text-sm text-muted-foreground mb-6">Pick your departure date to see the countdown and a dated task list.</p>}

      <div className="space-y-2">
        {schedule.map((item) => {
          const isDone = done.includes(item.task);
          const overdue = !isDone && item.offset < 0;
          return (
            <label key={item.task} className={`flex items-center gap-3 px-4 py-3 rounded-lg border cursor-pointer ${isDone ? "opacity-50 bg-muted/30" : overdue ? "border-red-300 bg-red-50 dark:bg-red-950/20" : "bg-card"}`}>
              <input type="checkbox" checked={isDone} onChange={() => toggle(item.task)} aria-label={`Mark "${item.task}" done`} />
              <span className={`text-sm ${isDone ? "line-through" : ""}`}>{item.task}</span>
              <span className={`text-xs ml-auto ${overdue ? "text-red-600 font-medium" : "text-muted-foreground"}`}>
                {overdue ? `Overdue · ` : ""}{item.due.toLocaleDateString(undefined, { month: "short", day: "numeric", year: item.due.getFullYear() !== today.getFullYear() ? "numeric" : undefined })}
              </span>
            </label>
          );
        })}
      </div>
      {schedule.length > 0 && <div className="flex justify-between items-center mt-3"><p className="text-xs text-muted-foreground">Kept for this visit only (erased when you close the tab).</p><Button size="sm" variant="ghost" onClick={resetDone}>Clear ticks</Button></div>}
      <BackupBar tool="travel-countdown-planner" prefixes={["fdp-trip-", "fdp-countdown-"]} session />
      <ToolInfoSection whatIsIt={TOOL_INFO_CONTENT.what} howToUse={TOOL_INFO_CONTENT.how} tip={TOOL_INFO_CONTENT.tip} />
    </ToolContainer>
  );
}
